import type { CompanyContentBoostType } from "@/modules/companies/client/api/contentBoostTypes"
import type { TrackEventModules, TrackEventProperties } from "@/modules/tracking/types"
import type { FunctionComponent } from "react"

import { CardCompanyContentBoost } from "@/modules/companies/client/components/CardCompanyContentBoost/CardCompanyContentBoost"
import { getArticleTranslationKey } from "@/modules/companies/client/utils/getArticleTranslationKey"
import { getEventTranslationKey } from "@/modules/companies/client/utils/getEventTranslationKey"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { TrackLink } from "@/modules/tracking/components/TrackLink"
import { TrackObserver } from "@/modules/tracking/components/TrackObserver"

import styles from "./CompanyContentBoost.module.css"

type CompanyContentBoostProps = {
  algoliaRuleUuid?: string
  contentBoost: CompanyContentBoostType
  trackingModule: TrackEventModules
}

export const CompanyContentBoost: FunctionComponent<CompanyContentBoostProps> = ({
  algoliaRuleUuid = "",
  contentBoost,
  trackingModule,
}) => {
  const { t } = useTranslation(["companies_client"])
  const locale = useLocale()

  const { article, event, id, company, contentType } = contentBoost

  let title = ""
  let previewUrl = ""
  let type = ""
  let href = ""

  if (article && contentType === "CONTENT_TYPE_ARTICLE") {
    title = article.title || ""
    previewUrl = article.previewUrl || ""
    type = getArticleTranslationKey(article.type, t)
    href = `/${locale}/${article.slug}`
  } else if (event && contentType === "CONTENT_TYPE_EVENT") {
    title = event.title || ""
    previewUrl = event.previewUrl || ""
    type = getEventTranslationKey(event.type, t)
    href = `/${locale}/${event.slug}`
  }

  const trackProperties: TrackEventProperties = {
    company_id: company.id,
    company_name: company.name,
    content_boost_id: id,
    content_category: contentBoost.article?.type || contentBoost.event?.type,
    content_type: contentType,
    module: trackingModule,
    rule_uuid: algoliaRuleUuid,
    service: "companies",
  }

  return (
    <TrackObserver
      eventName="content_boost_display"
      eventProperties={trackProperties}
      observerOptions={{ threshold: 0.8 }}
    >
      <TrackLink
        href={href}
        eventName="content_boost_click"
        eventProperties={trackProperties}
        className={styles.link}
        data-testid="company-content-boost-link"
      >
        <CardCompanyContentBoost title={title} type={type} previewUrl={previewUrl} company={company} />
      </TrackLink>
    </TrackObserver>
  )
}
